import supabase, { isSupabaseConfigured } from "./supabaseClient.js";

const LOCAL_BOOKINGS_STORAGE_KEY = "trishul_local_bookings";

function unavailable() {
  return {
    data: null,
    error: new Error(
      "Supabase is not configured. Add the VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY values.",
    ),
  };
}

export function getLocalBookings() {
  try {
    const raw = localStorage.getItem(LOCAL_BOOKINGS_STORAGE_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw);
    return Array.isArray(list) ? list : [];
  } catch (err) {
    console.warn("Could not read local bookings from storage", err);
    return [];
  }
}

export function saveLocalBooking(booking) {
  try {
    const list = getLocalBookings();
    const existingIndex = list.findIndex(
      (item) =>
        item.id === booking.id ||
        (item.reference && item.reference === booking.reference),
    );
    let updated;
    if (existingIndex >= 0) {
      updated = [...list];
      updated[existingIndex] = { ...updated[existingIndex], ...booking };
    } else {
      updated = [booking, ...list];
    }
    localStorage.setItem(LOCAL_BOOKINGS_STORAGE_KEY, JSON.stringify(updated));
    return booking;
  } catch (err) {
    console.warn("Could not save local booking to storage", err);
    return booking;
  }
}

export function updateLocalBooking(idOrRef, updates) {
  try {
    const list = getLocalBookings();
    const index = list.findIndex(
      (item) => item.id === idOrRef || item.reference === idOrRef,
    );
    if (index >= 0) {
      list[index] = { ...list[index], ...updates, updated_at: new Date().toISOString() };
      localStorage.setItem(LOCAL_BOOKINGS_STORAGE_KEY, JSON.stringify(list));
      return list[index];
    }
  } catch (err) {
    console.warn("Could not update local booking", err);
  }
  return null;
}

export async function createBooking({ userId, ...booking }) {
  if (!isSupabaseConfigured) return unavailable();
  return supabase
    .from("bookings")
    .insert({ customer_id: userId, ...booking })
    .select()
    .single();
}

export async function createEnquiry(enquiry) {
  if (!isSupabaseConfigured) return unavailable();
  return supabase.from("enquiries").insert(enquiry).select().single();
}

export async function getCustomerBookings(userId) {
  if (!isSupabaseConfigured) return unavailable();
  return supabase
    .from("bookings")
    .select("*, vehicles(name, category)")
    .eq("customer_id", userId)
    .order("created_at", { ascending: false });
}

export async function getCustomerEnquiries(userId) {
  if (!isSupabaseConfigured) return unavailable();
  return supabase
    .from("enquiries")
    .select("*, vehicles(name, category)")
    .eq("customer_id", userId)
    .order("created_at", { ascending: false });
}

export async function getPublicVehicles() {
  if (!isSupabaseConfigured) return { data: [], error: null };
  return supabase
    .from("vehicles")
    .select("id,name,category,seats,status")
    .eq("status", "available")
    .order("name");
}

/**
 * Saves a vehicle availability enquiry from the landing page.
 * Stores in localStorage so it always appears in Admin Dashboard,
 * and attempts to sync to Supabase bookings and enquiries tables.
 */
export async function saveVehicleAvailabilityEnquiry(entry) {
  const reference =
    entry.reference ||
    `TRI-${Math.random().toString(36).substring(2, 10).toUpperCase()}`;

  const nowIso = new Date().toISOString();
  const customerName =
    entry.customer_name ||
    (entry.mobile_number ? `Visitor (${entry.mobile_number})` : "Website Lead");

  const canonicalRecord = {
    id: entry.id || (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `local-${Date.now()}`),
    reference,
    customer_id: entry.customer_id || null,
    customer_name: customerName,
    mobile_number: entry.mobile_number || "",
    trip_type: entry.trip_type || "one_way",
    rental_type: entry.rental_type || "with_driver",
    pickup_date: entry.pickup_date,
    pickup_time: entry.pickup_time || "09:00",
    return_date: entry.return_date || null,
    return_time: entry.return_time || null,
    pickup_location: entry.pickup_location || "",
    drop_location: entry.drop_location || "",
    passengers: Number(entry.passengers) || 1,
    package_id: entry.package_id || null,
    vehicle_name: entry.vehicle_name || entry.vehicleType || "Any",
    requirements: entry.requirements || "",
    status: entry.status || "pending",
    notes: entry.notes || "Enquiry from landing page (WhatsApp Check Availability)",
    created_at: entry.created_at || nowIso,
    updated_at: entry.updated_at || nowIso,
    customer: {
      full_name: customerName,
      mobile: entry.mobile_number || "",
    },
    vehicle: {
      name: entry.vehicle_name || entry.vehicleType || "Any",
    },
  };

  // 1. Immediately save to localStorage so Admin has it with zero delay
  saveLocalBooking(canonicalRecord);

  // 2. If Supabase is configured, attempt to save to cloud
  if (isSupabaseConfigured) {
    try {
      const dbPayload = {
        reference: canonicalRecord.reference,
        customer_id: canonicalRecord.customer_id,
        mobile_number: canonicalRecord.mobile_number,
        trip_type: canonicalRecord.trip_type,
        rental_type: canonicalRecord.rental_type,
        pickup_date: canonicalRecord.pickup_date,
        pickup_time: canonicalRecord.pickup_time,
        return_date: canonicalRecord.return_date,
        return_time: canonicalRecord.return_time,
        pickup_location: canonicalRecord.pickup_location,
        drop_location: canonicalRecord.drop_location,
        passengers: canonicalRecord.passengers,
        package_id: canonicalRecord.package_id,
        requirements: canonicalRecord.requirements,
        status: canonicalRecord.status,
        notes: canonicalRecord.notes,
      };

      // Only pass customer_id if it exists, otherwise omit so DB default or null works
      if (!dbPayload.customer_id) {
        delete dbPayload.customer_id;
      }

      const { data: dbBooking, error: dbError } = await supabase
        .from("bookings")
        .insert(dbPayload)
        .select()
        .maybeSingle();

      if (dbBooking) {
        const merged = { ...canonicalRecord, ...dbBooking };
        saveLocalBooking(merged);
        return { data: merged, error: null };
      }

      if (dbError) {
        console.warn("Supabase booking insert notice:", dbError.message);
      }
    } catch (err) {
      console.warn("Supabase booking insert failed, saved to local storage", err);
    }

    // Also attempt saving to enquiries table
    try {
      await supabase.from("enquiries").insert({
        customer_id: canonicalRecord.customer_id || null,
        name: customerName,
        mobile: canonicalRecord.mobile_number,
        travel_date: canonicalRecord.pickup_date || null,
        return_date: canonicalRecord.return_date || null,
        pickup_location: canonicalRecord.pickup_location,
        destination: canonicalRecord.drop_location,
        passengers: canonicalRecord.passengers,
        message: canonicalRecord.requirements,
        status: "new",
      });
    } catch {
      // Ignore background enquiry backup error
    }
  }

  return { data: canonicalRecord, error: null };
}
